import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import AuthenticationContext from '@/context/AuthenticationContext';
import { BodyLargeMidEmphasis } from '@/design-system';
import { AccessibleDivButton } from '@/components';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import styles from './AccountCard.styles';

interface AccountCardProps {
  label: string;
  route: string;
  icon?: React.ReactNode;
  isExternal?: boolean;
  showArrow: boolean;
}

export const AccountCard: React.FC<AccountCardProps> = ({
  label,
  route,
  icon,
  isExternal = false,
  showArrow,
}) => {
  const { logout } = useContext(AuthenticationContext);
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const onClick = async () => {
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: 'account_page:navigation',
      label,
    });

    if (label === 'Logout') {
      await logout();
    }
    if (isExternal) {
      window.open(route, '_blank');
    } else {
      navigate(route);
    }
  };

  return (
    <AccessibleDivButton
      className={styles.container}
      onClick={onClick}
      data-testid="accountCard"
      aria-label={`Go to ${label}`}
    >
      <div className={styles.content} data-testid="accountCardContent">
        <span className={styles.icon}>{icon}</span>
        <BodyLargeMidEmphasis data-testid="accountLabel">{label}</BodyLargeMidEmphasis>
      </div>
      {showArrow && (
        <span className={styles.arrowIcon} aria-hidden="true">
          <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
        </span>
      )}
    </AccessibleDivButton>
  );
};
